export interface Cliente {
  id: number;
  empresaId: number;
  nombreEmpresa: string;
  clienteId: string;
  nombre: string;
  apellido: string;
  telefono: string;
  email: string | null;
  direccion: string | null;
  fechaNacimiento: string | null;
  genero: string | null;
  activo: boolean;
  fechaCreacion: string;
  fechaUltimaActualizacion: string | null;
  notas: string | null;
}

export interface CreateClienteDto {
  empresaId: number;
  clienteId: string;
  nombre: string;
  apellido: string;
  telefono: string;
  email: string | null;
  direccion: string | null;
  fechaNacimiento: string | null;
  genero: string | null;
  activo: boolean;
  notas: string | null;
}

export interface UpdateClienteDto {
  empresaId: number;
  clienteId: string;
  nombre: string;
  apellido: string;
  telefono: string;
  email: string | null;
  direccion: string | null;
  fechaNacimiento: string | null;
  genero: string | null;
  activo: boolean;
  notas: string | null;
}

export interface AutoRegistroClienteDto {
  telefono: string;
  nombre: string;
  direccion?: string | null;
}

export interface AutoRegistroClienteResponse {
  clienteId: number;
}

class ClientesService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async getAll(): Promise<Cliente[]> {
    return this.request<Cliente[]>('/clientes');
  }

  async getById(id: number): Promise<Cliente> {
    return this.request<Cliente>(`/clientes/${id}`);
  }

  async create(data: CreateClienteDto): Promise<Cliente> {
    return this.request<Cliente>('/clientes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async update(id: number, data: UpdateClienteDto): Promise<Cliente> {
    return this.request<Cliente>(`/clientes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<void>(`/clientes/${id}`, {
      method: 'DELETE',
    });
  }

  async autoRegistro(data: AutoRegistroClienteDto): Promise<AutoRegistroClienteResponse> {
    return this.request<AutoRegistroClienteResponse>('/clientes/auto-registro', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const clientesService = new ClientesService();
